import React, { Fragment } from 'react';
import { Layers } from '@wsp/map-gl';
import { Map as WSPReactMap } from '@wsp/map-react';
import PropTypes from 'prop-types';
import { mapConfig, mapLayerMetaData } from './config';
import { theme } from './config/theme';
import usStateGeoJSONData from './geo-data/usStateMapCoordinates';
/**
 * WSP map component
 * Memoized to prevent re-rendering due to Parent Comp. prop changes
 */
const WSPMap = React.memo((props) => {
	const { height, width, isMapLoaded, setIsMapLoaded, apiKey } = props;
	return (
		<Fragment>
			{apiKey ? (
				<WSPReactMap
					{...{
						isMapLoaded,
						setIsMapLoaded,
						apiKey,
						mapConfig,
						height,
						width,
					}}
				/>
			) : (
				<p>No API key provided!</p>
			)}
		</Fragment>
	);
});
WSPMap.displayName = 'WSPMap';
WSPMap.propTypes = {
	height: PropTypes.any,
	width: PropTypes.string,
	isMapLoaded: PropTypes.bool,
	setIsMapLoaded: PropTypes.func,
	apiKey: PropTypes.string,
};
/**
 * Default Map component
 */
function MapDefault(props) {
	const { height, width, geoJSONData, fillColor, tooltip, apiKey, onHover } =
		props;
	const [isMapLoaded, setIsMapLoaded] = React.useState(false);

	const mapProps = { height, width, isMapLoaded, setIsMapLoaded, apiKey };

	const [hoveredAreaId, setHoveredAreaId] = React.useState(null);

	const getLayerMetaData = () => {
		const metaData = { ...mapLayerMetaData };
		// Adds co-ordinates data
		metaData.data = geoJSONData;

		metaData.layerConfig.getFillColor = fillColor;

		metaData.layerConfig.getLineColor = (args) => {
			if (hoveredAreaId && args.properties.id === hoveredAreaId) {
				return [60, 34, 138];
			} else {
				return [255, 255, 255];
			}
		};

		metaData.layerConfig.getLineWidth = (args) => {
			if (hoveredAreaId && args.properties.id === hoveredAreaId) {
				return 5;
			} else {
				return 1;
			}
		};

		// Hover
		metaData.layerConfig.onHover = (info) => {
			if (info.object?.id) {
				setHoveredAreaId(info.object?.id);
			}
			onHover(info);
		};

		metaData.layerConfig.updateTriggers = {
			getFillColor: hoveredAreaId,
			getLineColor: hoveredAreaId,
			getLineWidth: hoveredAreaId,
		};

		return metaData;
	};
	const drawLayer = () => {
		Layers.add(getLayerMetaData());
	};
	// Once WSP map is loaded, this will update GMap styles, draw the layers
	// and creates tooltip
	React.useEffect(() => {
		if (isMapLoaded) {
			setTimeout(() => {
				updateMapStyles();
				drawLayer();
				attachTooltip();
			}, 200);
		}
	}, [isMapLoaded]);
	// This fires only when isMapAreaClickable is true
	// For every clicks(selectionId changes) on map, layers are drawn using new data
	React.useEffect(() => {
		if (hoveredAreaId !== null) {
			drawLayer();
		}
	}, [hoveredAreaId]);

	const attachTooltip = () => {
		window.mapGL.map.mapOverlay._deck?.setProps({
			getTooltip: tooltip,
		});
	};
	return <WSPMap {...mapProps} />;
}
// Remove other objects from Google Map by applying styles
// and restricts some opt.
const updateMapStyles = () => {
	const styles = new window.google.maps.StyledMapType(theme);
	window.mapGL.getMapInstance().mapTypes.set('styled_map', styles);
	window.mapGL.getMapInstance().setMapTypeId('styled_map');
	window.mapGL.getMapInstance().setOptions({
		maxZoom: 12,
		keyboardShortcuts: false,
	});
};

MapDefault.propTypes = {
	width: PropTypes.string,
	tooltip: PropTypes.func,
	onHover: PropTypes.func,
	apiKey: PropTypes.string,
	fillColor: PropTypes.func,
	geoJSONData: PropTypes.object,
	height: PropTypes.string.isRequired,
};

MapDefault.defaultProps = {
	width: 'auto',
	geoJSONData: usStateGeoJSONData,
	tooltip: () => ({
		html: 'No data available',
		style: {
			padding: '12px',
			width: '100px',
			borderRadius: '4px',
			background: 'rgba(255, 255, 255, 0.9)',
		},
	}),
	fillColor: () => [211, 200, 226],
};

export default MapDefault;
